import { difference, intersection, keyBy, mapValues, uniq } from 'es-toolkit';
import React, { useCallback, useMemo, useState } from 'react';
import {
  CustomSummary,
  CustomTreeData,
  DataTypeProvider,
  Filter,
  FilteringState,
  IntegratedFiltering,
  IntegratedSelection,
  IntegratedSorting,
  SelectionState,
  SortingState,
  SummaryState,
  TreeDataState,
} from '@devexpress/dx-react-grid';
import {
  ColumnChooser,
  Grid,
  TableColumnResizing,
  TableColumnVisibility,
  TableFilterRow,
  TableHeaderRow,
  TableSelection,
  TableSummaryRow,
  TableTreeColumn,
  Toolbar,
  VirtualTable,
} from '@devexpress/dx-react-grid-material-ui';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Autocomplete, Box, Paper, TableCell, TextField, Tooltip } from '@mui/material';
import { castDraft } from 'immer';

import { expandToFillParentStyles } from '@paragon/client-tools';
import { ProductionComponentInstanceStatus } from '@paragon/contract-library';
import { enqueueWarning, InlineNotification } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, humanCase, naturalSort, naturalSortByProperty } from '@paragon/utilities';

import { loadScheduleGroupData } from '../../../actions/loadScheduleGroupData';
import { getBundlesByScheduleGroupGuid } from '../../../selectors/getBundlesByScheduleGroupGuid';
import { getDistanceFormatterSelector } from '../../../selectors/getDistanceFormatterSelector';
import { getWarningsByBundleGuid } from '../../../selectors/getWarningsByBundleGuid';
import { getScheduleCheckedRowTotals, getScheduleGridRows } from '../../../selectors/scheduleGrid';
import {
  getScheduleGroupGuidFromRowId,
  GridRow,
  GridRowKey,
  isScheduleGroupRow,
} from '../../../selectors/scheduleGroupGrid';
import {
  checkRows,
  collapseRows,
  expandRows,
  setUserColumnWidths,
  uncheckRows,
} from '../../../store/scheduleGridSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ComponentPreviewCell } from '../ComponentPreviewCell';
import { defaultStatusList } from '../defaultStatusList';
import { ExpandCollapseButtons } from '../ExpandCollapseButtons';
import { GridRoot } from '../GridRoot';
import { Column } from '../gridTypes';
import { compareDates, distancesToString, formatDateToString, trimmedCaseInsensitiveFilter } from '../gridUtilities';
import { ScheduleGroupGridTableSummaryRowCell } from '../ScheduleGroupGridTableSummaryRowCell';
import { ScheduleGroupGridTableTreeColumnCell } from '../ScheduleGroupGridTableTreeColumnCell';
import {
  getAffectedAncestorRowIdsGetter,
  getDescendantRowsGetter,
  getFilterForSelfAndHierarchy,
} from '../scheduleGroupGridUtilities';
import { StatusesAsColorBar } from '../StatusesAsColorBar';
import { StatusesAsColoredText } from '../StatusesAsColoredText';
import { ScheduleGridRowActions } from './ScheduleGridRowActions';
import { ScheduleGridToolbarPlugin } from './ScheduleGridToolbarPlugin';

const statusColumnWidth = 170;

const baseColumnDefinitions: { [columnName: string]: Column<GridRow> } = {
  expandCollapseByScheduleGroup: {
    name: 'expandCollapseByScheduleGroup',
    title: 'Exp/Col',
    defaultWidth: 70,
    sortingEnabled: false,
    filteringEnabled: false,
  },
  name: {
    name: 'name',
    title: 'Name',
    defaultWidth: 450,
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
  },
  targetDate: {
    name: 'targetDate',
    title: 'Target Date',
    defaultWidth: 120,
    sortingEnabled: true,
    sortingCompare: compareDates,
    filteringEnabled: true,
    getCellValue: (row) => (row.type === 'scheduleGroup' && row.targetDate ? formatDateToString(row.targetDate) : null),
  },
  actions: {
    name: 'actions',
    title: 'Actions',
    defaultWidth: 100,
    sortingEnabled: false,
    filteringEnabled: false,
    getCellValue: (row) => <ScheduleGridRowActions row={row} />,
  },
  trussCount: {
    name: 'trussCount',
    title: 'Truss Count',
    defaultWidth: 120,
    align: 'right',
    sortingEnabled: true,
    filteringEnabled: false,
  },
  boardFeet: {
    name: 'boardFeet',
    title: 'BF',
    defaultWidth: 80,
    align: 'right',
    sortingEnabled: true,
    filteringEnabled: false,
    getCellValue: (row) => (row.boardFeet === null ? null : Math.round(row.boardFeet)),
  },
  linearFeet: {
    name: 'linearFeet',
    title: 'LF',
    defaultWidth: 80,
    align: 'right',
    sortingEnabled: true,
    filteringEnabled: false,
    getCellValue: (row) => (row.linearFeet === null ? null : Math.round(row.linearFeet)),
  },
  thicknesses: {
    name: 'thicknesses',
    title: 'Thickness',
    defaultWidth: 110,
    align: 'right',
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
  },
  heights: {
    name: 'heights',
    title: 'Height',
    defaultWidth: 90,
    align: 'right',
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
  },
  widths: {
    name: 'widths',
    title: 'Width',
    defaultWidth: 90,
    align: 'right',
    sortingEnabled: true,
    sortingCompare: naturalSort,
    filteringEnabled: true,
  },
  preview: {
    name: 'preview',
    title: 'Preview',
    defaultWidth: 100,
    sortingEnabled: false,
    filteringEnabled: false,
    getCellValue: (row) =>
      row.type === 'groupedProductionComponentInstance' ? (
        <ComponentPreviewCell analysisSetGuid={row.analysisSetGuid} maxWidth={100} maxHeight={25} />
      ) : null,
  },
  status: {
    name: 'status',
    title: 'Status',
    defaultWidth: statusColumnWidth,
    sortingEnabled: true,
    filteringEnabled: true,
    filteringPredicate: (value: any, filter: Filter, row: GridRow) => {
      const rowStatuses = row.statusPercentages.map((statusPercentage) => statusPercentage.status);
      const filterStatuses: readonly ProductionComponentInstanceStatus[] = filter.value;
      return intersection(rowStatuses, filterStatuses).length > 0;
    },
    getCellValue: (row) =>
      row.type === 'scheduleGroup' ? (
        <StatusesAsColorBar
          statusPercentages={row.statusPercentages}
          statusColumnWidth={statusColumnWidth}
          rowId={row.rowId}
        />
      ) : (
        <StatusesAsColoredText statusPercentages={row.statusPercentages} rowId={row.rowId} />
      ),
  },
  line: {
    name: 'line',
    title: 'Line',
    defaultWidth: 100,
    align: 'right',
    sortingEnabled: false,
    filteringEnabled: false,
  },
  truckload: {
    name: 'truckload',
    title: 'Truckload',
    defaultWidth: 120,
    align: 'right',
    sortingEnabled: false,
    filteringEnabled: true,
  },
};

function rowSelectionDisabled(row: GridRow) {
  const { type } = row;
  switch (type) {
    case 'scheduleGroup':
      return false;
    case 'bundle':
      return false;
    case 'groupedProductionComponentInstance':
      return true;
    default:
      assertExhaustiveSwitch(type);
  }
}

export function ScheduleGrid() {
  const dispatch = useAppDispatch();
  const rows = useAppSelector(getScheduleGridRows);
  const checkedRowIds = useAppSelector((state) => state.scheduleGrid.checkedRows);
  const expandedRowIds = useAppSelector((state) => state.scheduleGrid.expandedRows);
  const userColumnWidths = useAppSelector((state) => state.scheduleGrid.userColumnWidths);
  const bundlesByScheduleGroupGuid = useAppSelector(getBundlesByScheduleGroupGuid);
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const [gridFilters, setGridFilters] = useState<Filter[]>([]);

  const { checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, thicknesses, heights } =
    useAppSelector(getScheduleCheckedRowTotals);

  const rowsKeyedByRowId = useMemo(() => keyBy(rows, (row) => row.rowId), [rows]);

  const getDescendantRows = getDescendantRowsGetter(rowsKeyedByRowId, bundlesByScheduleGroupGuid);
  const filterForSelfAndHierarchy = getFilterForSelfAndHierarchy(rowsKeyedByRowId, bundlesByScheduleGroupGuid);
  const getAffectedAncestorRowIds = getAffectedAncestorRowIdsGetter(
    rowsKeyedByRowId,
    bundlesByScheduleGroupGuid,
    checkedRowIds,
  );

  const columns: readonly Column<GridRow>[] = useMemo(
    () => [
      {
        ...baseColumnDefinitions['expandCollapseByScheduleGroup'],
        getCellValue: (row) => {
          if (row.type !== 'scheduleGroup') return null;
          const expandableDescendantRowIds = getDescendantRows(row.rowId).map(({ rowId }) => rowId);
          return (
            <ExpandCollapseButtons
              rowId={row.rowId}
              expandRows={(rowIds) => {
                dispatch(expandRows(rowIds));
                dispatch(loadScheduleGroupData(row.guid));
              }}
              collapseRows={(rowIds) => void dispatch(collapseRows(rowIds))}
              expandableDescendantRowIds={expandableDescendantRowIds}
              expandedRowIds={expandedRowIds}
            />
          );
        },
      },
      {
        ...baseColumnDefinitions['name'],
        filteringPredicate: filterForSelfAndHierarchy((row, filter) => trimmedCaseInsensitiveFilter(row.name, filter)),
      },
      baseColumnDefinitions['targetDate'],
      baseColumnDefinitions['actions'],
      baseColumnDefinitions['trussCount'],
      baseColumnDefinitions['boardFeet'],
      baseColumnDefinitions['linearFeet'],
      {
        ...baseColumnDefinitions['thicknesses'],
        filteringPredicate: filterForSelfAndHierarchy((row, filter) =>
          trimmedCaseInsensitiveFilter(distancesToString(row.thicknesses, distanceFormatter), filter),
        ),
        getCellValue: (row) => distancesToString(row.thicknesses, distanceFormatter),
      },
      {
        ...baseColumnDefinitions['heights'],
        filteringPredicate: filterForSelfAndHierarchy((row, filter) =>
          trimmedCaseInsensitiveFilter(distancesToString(row.heights, distanceFormatter), filter),
        ),
        getCellValue: (row) => distancesToString(row.heights, distanceFormatter),
      },
      {
        ...baseColumnDefinitions['widths'],
        filteringPredicate: filterForSelfAndHierarchy((row, filter) =>
          trimmedCaseInsensitiveFilter(distancesToString(row.widths, distanceFormatter), filter),
        ),
        getCellValue: (row) => distancesToString(row.widths, distanceFormatter),
      },
      baseColumnDefinitions['preview'],
      baseColumnDefinitions['status'],
      baseColumnDefinitions['line'],
      {
        ...baseColumnDefinitions['truckload'],
        filteringPredicate: filterForSelfAndHierarchy((row, filter) =>
          trimmedCaseInsensitiveFilter(row.truckload, filter),
        ),
      },
    ],
    [filterForSelfAndHierarchy, getDescendantRows, distanceFormatter, dispatch, expandedRowIds],
  );
  const keyedColumns = useMemo(() => keyBy(columns, (column) => column.name), [columns]);
  const columnSortingExtensions = useMemo(
    () =>
      columns.map((column) => ({
        columnName: column.name,
        sortingEnabled: column.sortingEnabled,
        compare: column.sortingCompare,
      })),
    [columns],
  );
  const columnFilteringExtensions = useMemo(
    () =>
      columns.map((column) => ({
        columnName: column.name,
        filteringEnabled: column.filteringEnabled,
        predicate: column.filteringPredicate,
      })),
    [columns],
  );
  const getFilteredRows = useCallback(
    (filters: readonly Filter[]) =>
      rows.filter((row) =>
        filters.every((filter) => {
          const column = keyedColumns[filter.columnName];
          if (!column.filteringPredicate) return true;
          return column.filteringPredicate(row[column.name as GridRowKey], filter, row);
        }),
      ),
    [rows, keyedColumns],
  );

  function onFilter(filters: Filter[]) {
    const filteredRows = getFilteredRows(gridFilters);
    const newFilteredRows = getFilteredRows(filters);
    const newlyHiddenCheckedRowIds = filteredRows
      .filter(
        (row) => !newFilteredRows.includes(row) && checkedRowIds.includes(row.rowId) && !rowSelectionDisabled(row),
      )
      .map((row) => row.rowId);
    if (newlyHiddenCheckedRowIds.length > 0) {
      dispatch(enqueueWarning('Some selected rows are now hidden'));
    }

    setGridFilters(filters);
  }

  function onExpandedRowsChange(newExpandedRows: (string | number)[]) {
    for (const newlyCollapsedRow of difference(expandedRowIds, newExpandedRows)) {
      dispatch(collapseRows([newlyCollapsedRow.toString()]));
    }

    for (const newlyExpandedRow of difference(newExpandedRows, expandedRowIds)) {
      const newlyExpandedRowString = newlyExpandedRow.toString();
      if (isScheduleGroupRow(newlyExpandedRowString)) {
        dispatch(loadScheduleGroupData(getScheduleGroupGuidFromRowId(newlyExpandedRowString)));
      }
      dispatch(expandRows([newlyExpandedRowString]));
    }
  }

  function onCheckedRowsChange(newCheckedRows: (string | number)[]) {
    for (const uncheckedRowStringOrNumber of difference(checkedRowIds, newCheckedRows)) {
      const uncheckedRowId = uncheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(rowsKeyedByRowId[uncheckedRowId])) continue;

      const descendantRowIds = getDescendantRows(uncheckedRowId).map((row) => row.rowId);
      dispatch(
        uncheckRows([uncheckedRowId].concat(descendantRowIds).concat(getAffectedAncestorRowIds(uncheckedRowId))),
      );
    }

    for (const newlyCheckedRowStringOrNumber of difference(newCheckedRows, checkedRowIds)) {
      const newlyCheckedRowId = newlyCheckedRowStringOrNumber.toString();

      if (rowSelectionDisabled(rowsKeyedByRowId[newlyCheckedRowId])) continue;

      const descendantRows = getDescendantRows(newlyCheckedRowId);
      const filteredRows = getFilteredRows(gridFilters);
      const visibleDescendantRowIds = descendantRows
        .filter((row) => filteredRows.includes(row))
        .map((row) => row.rowId);
      // When bundles are being checked, we always want to include all of their children, even if some are hidden by a
      // filter.
      const invisibleBundleDescendantRowIds = [newlyCheckedRowId]
        .concat(visibleDescendantRowIds)
        .filter((rowId) => rowsKeyedByRowId[rowId].type === 'bundle')
        .flatMap((rowId) => getDescendantRows(rowId))
        .map((row) => row.rowId)
        .filter((rowId) => !visibleDescendantRowIds.includes(rowId));

      // In the case that the user has selected the parent to select all the visible children, the parent is not
      // actually checked, but shown as indeterminate. The perceived intention of selecting the parent again is to
      // deselect all the visible children. This block handles this special case.
      const allVisibleDescendantRowsChecked = visibleDescendantRowIds.every((rowId) => checkedRowIds.includes(rowId));
      if (allVisibleDescendantRowsChecked) {
        dispatch(uncheckRows(visibleDescendantRowIds.concat(invisibleBundleDescendantRowIds)));
        return;
      }

      const affectedAncestorRowIds = getAffectedAncestorRowIds(newlyCheckedRowId);
      const rowIdsToCheck = visibleDescendantRowIds
        .concat(invisibleBundleDescendantRowIds)
        .concat(affectedAncestorRowIds);
      const checkedRowIdsWithNewlyCheckedRowIds = checkedRowIds.concat(rowIdsToCheck);
      const allDescendantRowsChecked = descendantRows.every((row) =>
        checkedRowIdsWithNewlyCheckedRowIds.includes(row.rowId),
      );
      if (allDescendantRowsChecked) rowIdsToCheck.push(newlyCheckedRowId);
      dispatch(checkRows(rowIdsToCheck));
    }
  }

  return (
    <Paper sx={{ flex: 2, ...expandToFillParentStyles.containerStyleProps }}>
      <Grid rows={rows} columns={columns} getRowId={(row) => row.rowId} rootComponent={GridRoot}>
        <DataTypeProvider for={['name']} formatterComponent={NameDataTypeProvider} />
        <DataTypeProvider for={['status']} editorComponent={StatusFilter} />

        <TreeDataState expandedRowIds={castDraft(expandedRowIds)} onExpandedRowIdsChange={onExpandedRowsChange} />

        <SortingState
          defaultSorting={[
            { columnName: 'targetDate', direction: 'asc' },
            { columnName: 'thickness', direction: 'asc' },
            { columnName: 'name', direction: 'asc' },
          ]}
          columnExtensions={columnSortingExtensions}
        />
        <IntegratedSorting columnExtensions={columnSortingExtensions} />

        <FilteringState filters={gridFilters} onFiltersChange={onFilter} columnExtensions={columnFilteringExtensions} />
        <IntegratedFiltering columnExtensions={columnFilteringExtensions} />

        <SelectionState selection={castDraft(checkedRowIds)} onSelectionChange={onCheckedRowsChange} />
        <IntegratedSelection />

        <CustomTreeData
          getChildRows={(currentRow, rootRows) => {
            if (!currentRow) {
              return rootRows.filter((row) => row.type === 'scheduleGroup');
            }
            if (currentRow.type === 'scheduleGroup') {
              const childRows = rootRows.filter(
                (row) => row.type === 'bundle' && row.scheduleGroupGuid === currentRow.guid,
              );
              return childRows.length > 0 ? childRows : null;
            }
            if (currentRow.type === 'bundle') {
              const childRows = rootRows.filter(
                (row) => row.type === 'groupedProductionComponentInstance' && row.bundleGuid === currentRow.guid,
              );
              return childRows.length > 0 ? childRows : null;
            }
            return null;
          }}
        />

        <SummaryState
          totalItems={[
            { columnName: 'trussCount', type: '' },
            { columnName: 'boardFeet', type: '' },
            { columnName: 'linearFeet', type: '' },
            { columnName: 'thicknesses', type: '' },
            { columnName: 'heights', type: '' },
            { columnName: 'widths', type: '' },
          ]}
        />
        <CustomSummary
          totalValues={[checkedTrussCountTotal, checkedBoardFeetTotal, checkedLinearFeetTotal, thicknesses, heights]}
        />

        <VirtualTable columnExtensions={columns.map((column) => ({ columnName: column.name, align: column.align }))} />

        <TableColumnResizing
          columnWidths={
            userColumnWidths ?? columns.map((column) => ({ columnName: column.name, width: column.defaultWidth }))
          }
          onColumnWidthsChange={(newUserColumnWidths) => void dispatch(setUserColumnWidths(newUserColumnWidths))}
        />
        <TableHeaderRow showSortingControls />
        <TableFilterRow cellComponent={TableFilterCell} />
        <TableColumnVisibility />
        <Toolbar />
        <ScheduleGridToolbarPlugin />
        <ColumnChooser />
        <TableTreeColumn
          for="name"
          showSelectionControls
          cellComponent={(props) => (
            <ScheduleGroupGridTableTreeColumnCell
              rows={rows}
              checkedRowIds={checkedRowIds}
              rowSelectionDisabled={rowSelectionDisabled}
              {...props}
            />
          )}
        />
        {checkedRowIds.length > 0 && <TableSummaryRow itemComponent={ScheduleGroupGridTableSummaryRowCell} />}
        <TableSelection selectByRowClick showSelectionColumn={false} />
      </Grid>
    </Paper>
  );
}

function NameDataTypeProvider({ row }: DataTypeProvider.ValueFormatterProps) {
  const { type, guid, name } = row as GridRow;
  const bundlesByScheduleGroupGuid = useAppSelector(getBundlesByScheduleGroupGuid);
  const warningsByBundleGuid = useAppSelector(getWarningsByBundleGuid);

  const warningShortMessagesByScheduleGroupGuid = useMemo(
    () =>
      mapValues(bundlesByScheduleGroupGuid, (bundlesForScheduleGroup) =>
        uniq(
          bundlesForScheduleGroup.flatMap((bundle) =>
            warningsByBundleGuid[bundle.guid].map((bundleWarning) => bundleWarning.shortMessage),
          ),
        ),
      ),
    [bundlesByScheduleGroupGuid, warningsByBundleGuid],
  );

  switch (type) {
    case 'scheduleGroup':
      return (warningShortMessagesByScheduleGroupGuid[guid]?.length ?? 0) > 0 ? (
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <ReportProblemIcon sx={{ mr: 1 }} />
          <span>{name}</span>
          {naturalSortByProperty(warningShortMessagesByScheduleGroupGuid[guid], (message) => message).map(
            (warningShortMessage) => (
              <InlineNotification sx={{ my: 0, ml: 1 }} type="error" key={warningShortMessage}>
                {warningShortMessage}
              </InlineNotification>
            ),
          )}
        </Box>
      ) : (
        <span>{name}</span>
      );
    case 'bundle':
      return warningsByBundleGuid[guid].length > 0 ? (
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          <span>{name}</span>
          {naturalSortByProperty(warningsByBundleGuid[guid], (warning) => warning.shortMessage).map((warning) => (
            <Tooltip disableInteractive sx={{ ml: 1 }} title={warning.longMessage} key={warning.longMessage}>
              <span>
                <InlineNotification sx={{ my: 0, ml: 1 }} type="error">
                  {warning.shortMessage}
                </InlineNotification>
              </span>
            </Tooltip>
          ))}
        </Box>
      ) : (
        <span>{name}</span>
      );
    case 'groupedProductionComponentInstance':
      return <span>{name}</span>;
    default:
      assertExhaustiveSwitch(type);
  }
}

function StatusFilter({ value, onValueChange }: DataTypeProvider.ValueEditorProps) {
  return (
    <Autocomplete
      sx={{ width: statusColumnWidth }}
      multiple
      size="small"
      options={defaultStatusList}
      getOptionLabel={(status) => humanCase(status)}
      renderInput={(params) => <TextField {...params} variant="outlined" label="Filter..." />}
      value={value}
      onChange={(event, statuses) => void onValueChange(statuses)}
    />
  );
}
function TableFilterCell(props: TableFilterRow.CellProps) {
  return baseColumnDefinitions[props.column.name].filteringEnabled ? <TableFilterRow.Cell {...props} /> : <TableCell />;
}
